import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import { LEAVE_REVIEW_ENTRYPOINT } from "@src/appV2/PostShiftReview/types";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { type WorkplaceModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { useOtherWorkplaces } from "@src/appV2/redesign/ShiftDiscovery/WorkplacesList/useOtherWorkplaces";
import {
  Input,
  Text,
  Title,
  type UseModalState,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { UNLOCK_REVIEWS_ORIGIN, type UnlockReviewsLocationState } from "./navigation";
import { NoPriorWorkplacesBottomSheet } from "./NoPriorWorkplacesBottomSheet";
import { WorkplaceSearchItem } from "./WorkplaceSearchItem";

interface SelectedWorkplace {
  id: string;
  name: string;
  type: string;
}

type UnlockReviewsStep = "search" | "review";

interface UnlockReviewsPageProps {
  modalState: UseModalState;
}

export function UnlockReviewsPage(props: UnlockReviewsPageProps) {
  const { modalState } = props;

  const history = useHistory();
  const location = useLocation<UnlockReviewsLocationState>();
  const { workplaceId: originatingWorkplaceId } = useParams<WorkplaceModalRoutePathParams>();
  const { generateModalPath } = useWorkplaceModalsDataContext();

  const [step, setStep] = useState<UnlockReviewsStep>("search");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWorkplace, setSelectedWorkplace] = useState<SelectedWorkplace | undefined>();
  const noPriorWorkplacesModalState = useModalState();

  function handleUnlockComplete() {
    if (location.state?.unlockReviewsOrigin === UNLOCK_REVIEWS_ORIGIN.REVIEWS_MODAL) {
      history.goBack();
      return;
    }

    history.replace(
      generateModalPath(SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH, {
        workplaceId: originatingWorkplaceId,
      })
    );
  }

  useLogEffect(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_WORKPLACE_SEARCH_OPENED, {
    originatingWorkplaceId,
  });

  const debouncedSearchTerm = useDebounce({ value: searchTerm, debounceTimeInMs: 500 });
  const trimmedSearch = debouncedSearchTerm.trim();

  const { data: workplacesData, isLoading } = useOtherWorkplaces(
    {
      filter: { query: trimmedSearch || undefined },
      page: { size: 20 },
    },
    { enabled: trimmedSearch.length >= 2 }
  );

  const workplaces = useMemo(() => {
    if (!workplacesData) {
      return [];
    }

    return workplacesData.pages.flatMap((page) => page.data);
  }, [workplacesData]);

  function handleSelectWorkplace(workplaceId: string) {
    const workplace = workplaces.find((w) => w.id === workplaceId);
    if (!isDefined(workplace)) {
      return;
    }

    setSelectedWorkplace({
      id: workplace.id,
      name: workplace.attributes?.name ?? "",
      type: workplace.attributes?.type ?? "Unknown",
    });

    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_WORKPLACE_SELECTED, {
      workplaceId,
      workplaceName: workplace.attributes?.name,
      originatingWorkplaceId,
    });
  }

  function handleContinue() {
    if (isDefined(selectedWorkplace)) {
      setStep("review");
    }
  }

  function handleNoPriorWorkplaces() {
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_NO_PRIOR_WORKPLACES_TAPPED, {
      originatingWorkplaceId,
    });
    noPriorWorkplacesModalState.openModal();
  }

  if (step === "review" && isDefined(selectedWorkplace)) {
    return (
      <PostShiftReviewFlow
        modalState={modalState}
        facility={selectedWorkplace}
        source={LEAVE_REVIEW_ENTRYPOINT.UNLOCK_REVIEWS}
        originatingWorkplaceId={originatingWorkplaceId}
        onComplete={() => handleUnlockComplete()}
      />
    );
  }

  return (
    <>
      <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
        <StickyScrollPageHeader headerVariant="tertiary" title="Leave a Review" isLoading={false} />

        <Stack sx={{ overflow: "auto", height: "100%", paddingX: 7, paddingTop: 5 }}>
          <Stack spacing={4} sx={{ paddingBottom: 8 }}>
            <Title bold variant="h4" component="h2">
              Which facility?
            </Title>
            <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
              Enter the name of a facility you&apos;ve worked at before.
            </Text>
          </Stack>

          <Input
            iconType="search"
            placeholder="Search facilities..."
            size="small"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setSelectedWorkplace(undefined);
            }}
          />

          <Stack sx={{ marginTop: 4, flexGrow: 1 }}>
            {isLoading && trimmedSearch.length >= 2 && (
              <Text variant="body2" sx={{ paddingTop: 7, textAlign: "center" }}>
                Searching...
              </Text>
            )}

            {!isLoading && trimmedSearch.length >= 2 && workplaces.length === 0 && (
              <Text variant="body2" sx={{ paddingTop: 7, textAlign: "center" }}>
                No workplaces found
              </Text>
            )}

            {workplaces.map((workplace) => (
              <WorkplaceSearchItem
                key={workplace.id}
                workplaceId={workplace.id}
                name={workplace.attributes?.name ?? ""}
                searchTerm={trimmedSearch}
                isSelected={selectedWorkplace?.id === workplace.id}
                onSelect={(workplaceId) => handleSelectWorkplace(workplaceId)}
              />
            ))}
          </Stack>
        </Stack>

        <DialogFooter withShadow orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isDefined(selectedWorkplace)}
            onClick={() => handleContinue()}
          >
            Continue
          </Button>
          <TextButton sx={{ alignSelf: "center" }} onClick={() => handleNoPriorWorkplaces()}>
            I don&apos;t have any workplaces to review
          </TextButton>
        </DialogFooter>
      </FullScreenDialog>

      <NoPriorWorkplacesBottomSheet
        modalState={noPriorWorkplacesModalState}
        originatingWorkplaceId={originatingWorkplaceId}
        onUnlocked={() => handleUnlockComplete()}
      />
    </>
  );
}
